/* eslint-disable max-len */
/* eslint-disable import/no-named-default */
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Select } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import { SelectValue } from 'antd/lib/select';
import JsonQuery from 'json-query';
import { isArray, uniqBy, find, pick } from 'lodash';
import parse from 'parse-json';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    hooks as commonHooks,
    types as commonTypes,
    utils as commonUtils,
} from '@sitebuilder/common';
import {
    getTextSchemaProperties,
    getStyleSchemaProperties,
    getLayoutSchemaProperties,
} from '../utils';
import dropDownStyles, { themeDropDownStyles } from './dropDown.styles';
import DropDownOptionWrapper from './DropDownOptionWrapper';
import { FIRSTOPTION, PLACEHOLDER, CURRENTOPTION } from './index';

export interface optionType {
    label: string;
    pathExt: string;
}

export interface DropDownListOpt {
    displayName: string;
    [key: string]: any;
}

type defaultOptions = typeof PLACEHOLDER | typeof FIRSTOPTION | typeof CURRENTOPTION;
export interface DropDownProps {
    placeholder?: string;
    showSearch?: boolean;
    customDropOptionLength?: number;
    defaultOption: defaultOptions;
    supportSecondaryData: boolean;
    style: string;
    primaryDataKey: { [key: string]: any }[] | null;
    primaryDisplayName: string;
    multipleNameSeparator: string;
    primaryRedirectRef: string;
    primaryLabel?: string;
    secRedirectRef: string;
    secondaryDataKey?: { [key: string]: any }[] | null;
    secondaryDisplayName?: string;
    multipleSecNameSeparator?: string;
    secLabel?: string;
    secPlaceholder?: string;
    currentItemDatakey: string;
    secDefaultOption: defaultOptions;
    secCurrentItemDatakey: string;
    transformData?: boolean;
    functionToTransformData?: string;
    primaryAriaLabel?: string;
    secondaryAriaLabel?: string;
    targetTop: boolean;
    topURLDataKey: string;
    topURLDataOption: string;
    typographyClass?: string;
}

interface WidgetProperties extends commonTypes.WidgetPropertiesBase, DropDownProps {}

const DropDownWrapper = (props: commonTypes.WidgetInstanceProps): JSX.Element => {
    const { isClient } = commonHooks.useAppContext();
    const widgetProperties = commonHooks.useWidgetProperties<WidgetProperties>(
        props.widgetInstanceId
    );
    const {
        styles,
        primaryDataKey,
        primaryRedirectRef,
        primaryDisplayName,
        primaryLabel,
        secondaryDataKey,
        secRedirectRef,
        secondaryDisplayName,
        secLabel,
        secPlaceholder,
        placeholder,
        defaultOption,
        supportSecondaryData,
        showSearch,
        customDropOptionLength,
        ...rest
    } = widgetProperties || {};

    const { pathname } = useLocation();
    const i18n = commonHooks.useLocalization();

    const data: any = props.data;
    const { value: primary }: any = JsonQuery(`${primaryDataKey}`, { data });

    const secDropDownRef = useRef<any>(null);

    // utility function for redirect
    const updateProfileId = (e: string, selectedOption?: DropDownListOpt): void => {
        // check if page is within iframe
        if (rest.targetTop && isClient && window?.self !== window?.top) {
            let url = '';
            const topURLDataKey = i18n.format(rest.topURLDataKey, data);

            if (selectedOption && rest.topURLDataOption) {
                url = JsonQuery(`${topURLDataKey}`, { data: selectedOption })?.value;
            } else {
                const topUrlData = JsonQuery(`${topURLDataKey}`, { data })?.value;
                url = find(topUrlData, (d: any) => d?.entityId?.includes(e))?.url;
            }

            // navigate to parent window
            if (window.top) window.top.location.href = url;
        } else {
            // navigate within iframe
            const newSearchParams = `?profileId=${e}&locale=${data?.locale}`;

            if (window.location.search !== newSearchParams) {
                window.location.href = `${pathname}${newSearchParams}`;
            }
        }
    };

    let isRender: any = {
        primaryData: primary,
    };

    // get dropdown display names
    const _primaryDisplayNames = primaryDisplayName?.split(',');
    const _secDisplayNames = secondaryDisplayName?.split(',');

    const primaryList = useMemo(
        () => parse(JSON.stringify(isRender?.primaryData) || null),
        [isRender?.primaryData]
    );
    const _primaryLabel = i18n.format(primaryLabel || '', data);
    const _secLabel = i18n.format(secLabel || '', data);
    const placeholderVal = i18n.format(placeholder || '', data);
    const secPlaceholderVal = i18n.format(secPlaceholder || '', data);

    const primaryDropDownAriaLabel = i18n.format(rest.primaryAriaLabel || '', data);
    const secondaryDropDownAriaLabel = i18n.format(rest.secondaryAriaLabel || '', data);

    const primaryId = uuidv4();
    const secondaryId = uuidv4();

    const { Option } = Select;

    const [primaryValue, setPrimaryValue] = useState('');

    isRender = {
        ...isRender,
        primaryList,
        _primaryDisplayNames,
        multipleNameSeparator: rest.multipleNameSeparator,
        multipleSecNameSeparator: rest.multipleSecNameSeparator,
    };

    const _primaryList = uniqBy(
        uniqBy(isRender.primaryList, (e) => pick(e, isRender._primaryDisplayNames))?.map(
            (opt: any) => {
                const displayNames: string[] =
                    _primaryDisplayNames
                        ?.map((n) => JsonQuery(n, { data: opt })?.value)
                        .filter((n) => n) || [];
                let displayName = '';

                if (displayNames?.length > 0) {
                    displayName = displayNames.join(isRender.multipleNameSeparator);
                } else {
                    displayName = displayNames[0];
                }

                return {
                    ...opt,
                    displayName,
                };
            }
        ),
        'displayName'
    );

    // get secondary dropdown list items
    const _secondaryList = supportSecondaryData
        ? uniqBy(isRender.primaryList, (e) => pick(e, isRender._primaryDisplayNames))?.map(
              (opt: any) => {
                  // get primary dropdown display name
                  const primaryDisplayNames: string[] =
                      isRender._primaryDisplayNames
                          ?.map((n: string) => JsonQuery(n, { data: opt })?.value)
                          .filter((n: string) => n) || [];

                  let primaryDisplayName = '';

                  if (primaryDisplayNames?.length > 0) {
                      primaryDisplayName = primaryDisplayNames.join(isRender.multipleNameSeparator);
                  } else {
                      primaryDisplayName = primaryDisplayNames[0];
                  }

                  const displayNames: string[] =
                      _secDisplayNames
                          ?.map((n) => JsonQuery(n, { data: opt })?.value)
                          .filter((n) => n) || [];
                  let displayName = '';

                  if (displayNames?.length > 0) {
                      displayName = displayNames.join(isRender.multipleSecNameSeparator);
                  } else {
                      displayName = displayNames[0];
                  }

                  return {
                      ...opt,
                      displayName: primaryDisplayName, // assign primary dropdown display name to list
                      data: {
                          primaryDisplayName,
                          ref: JsonQuery(secRedirectRef, { data: opt })?.value, // assign secondary dropdown reference to redirect
                          displayName, // assign secondary dropdown display name to list
                      },
                  };
              }
          )
        : [];

    isRender = { ...isRender, _primaryList, _secondaryList };

    const transformFunctionName = `${props.widgetInstanceId}-transform`;

    try {
        // eslint-disable-next-line no-new-func
        const _transformedData =
            rest.transformData &&
            commonUtils.transformDataWithGlobals(
                transformFunctionName,
                rest.functionToTransformData,
                {
                    data,
                    JsonQuery,
                    ...isRender,
                }
            );

        if (_transformedData) {
            isRender = { ...isRender, ..._transformedData };
        }
    } catch (e) {
        console.log(e);
    }

    // default value for primary dropdown
    let defaultValue = '';

    if (defaultOption === CURRENTOPTION) {
        // set current option for primary dropdown when no secondary dropdown
        defaultValue = find(isRender._primaryList, (list) => list[rest.currentItemDatakey])?.[
            _primaryDisplayNames[0]
        ];
    } else if (defaultOption === FIRSTOPTION) {
        defaultValue = isRender._primaryList?.[0]?.[_primaryDisplayNames[0]];
    }

    useEffect(() => {
        if (defaultValue) setPrimaryValue(defaultValue);
    }, [defaultValue]);

    // default value for secondary dropdown
    let secDefaultValue: any = [];

    if (rest.secDefaultOption === CURRENTOPTION) {
        const listItem = find(isRender._secondaryList, (list) => list[rest.secCurrentItemDatakey]);

        // set current  option for primary dropdown when showing secondary dropdown
        defaultValue = listItem?.displayName;

        // set current option for secondary dropdown
        secDefaultValue = listItem?.data?.displayName;
    } else if (rest.secDefaultOption === FIRSTOPTION) {
        secDefaultValue = isRender._secondaryList?.[0]?.data?.displayName;
    }

    const handlePrimaryChange = (e: string | number) => {
        const selectedOption: DropDownListOpt = isRender._primaryList[e];
        const id = JsonQuery(primaryRedirectRef, { data: selectedOption })?.value;

        if (supportSecondaryData) {
            setPrimaryValue(selectedOption?.displayName);
            secDropDownRef?.current?.focus();
        } else {
            id && updateProfileId(id, selectedOption);
        }
    };

    const handleSecChange = (e: SelectValue) => {
        e && updateProfileId(e as string);
    };

    const handleSecClick = () => {
        if (isRender?._secondaryList?.length && primaryValue) {
            secDropDownRef?.current?.focus();
        }
    };

    const theme = data?.themeSelected || props?.theme;

    return (
        <div
            className={widgetProperties?.typographyClass ? widgetProperties.typographyClass : ''}
            css={css`
                ${!theme ? dropDownStyles : themeDropDownStyles}${getTextSchemaProperties(
                    widgetProperties,
                    data?.colorSpace
                )}${getStyleSchemaProperties(widgetProperties)}${getLayoutSchemaProperties(
                    widgetProperties
                )}${styles || ''}
            `}
            data-testid={'dropdown'}
        >
            <>
                {_primaryLabel && <h5 className="primary-label">{_primaryLabel}</h5>}
                <label htmlFor={`${primaryId}-primary-drop-down-label`} className="sr-only">
                    {primaryDropDownAriaLabel || ''}
                </label>
                <Select
                    dropdownStyle={{ position: 'fixed', borderRadius: '2px' }}
                    placeholder={defaultOption === PLACEHOLDER ? placeholderVal : undefined}
                    showSearch={showSearch}
                    optionFilterProp="children"
                    defaultOpen={false}
                    id={`${primaryId}-primary-drop-down-label`}
                    aria-label={primaryDropDownAriaLabel || ''}
                    defaultValue={defaultOption === PLACEHOLDER ? undefined : defaultValue}
                    onChange={(e: any) => handlePrimaryChange(e)}
                    className={'ant-select-custom selection primary'}
                    dropdownMatchSelectWidth={customDropOptionLength || false}
                    dropdownRender={(o: any) => (
                        <DropDownOptionWrapper style={styles}>{o}</DropDownOptionWrapper>
                    )}
                >
                    {isArray(isRender._primaryList) &&
                        isRender._primaryList?.map((option: DropDownListOpt, i: number) => {
                            return (
                                <Option className={'select-option'} key={i} value={i}>
                                    {option?.displayName}
                                </Option>
                            );
                        })}
                </Select>
                {supportSecondaryData && (
                    <>
                        {_secLabel && <h5 className="sec-label">{_secLabel}</h5>}
                        <label
                            htmlFor={`${secondaryId}-secondary-drop-down-label`}
                            className="sr-only"
                        >
                            {secondaryDropDownAriaLabel || ''}
                        </label>
                        <Select
                            ref={secDropDownRef}
                            dropdownStyle={{ position: 'fixed', borderRadius: '2px' }}
                            className="selection secondary"
                            onChange={handleSecChange}
                            showAction={['click']}
                            onClick={handleSecClick}
                            id={`${secondaryId}-secondary-drop-down-label`}
                            aria-label={secondaryDropDownAriaLabel || ''}
                            placeholder={
                                rest.secDefaultOption === PLACEHOLDER
                                    ? secPlaceholderVal
                                    : undefined
                            }
                            defaultValue={secDefaultValue}
                        >
                            {isArray(isRender._secondaryList) &&
                                isRender._secondaryList
                                    ?.filter(
                                        (list: DropDownListOpt) =>
                                            list?.displayName === primaryValue
                                    )
                                    .map((secOption: DropDownListOpt, i: number) => {
                                        return (
                                            <Option
                                                className={'select-option-sec'}
                                                key={i}
                                                value={secOption?.data?.ref}
                                            >
                                                {secOption?.data?.displayName}
                                            </Option>
                                        );
                                    })}
                        </Select>
                    </>
                )}
            </>
        </div>
    );
};

export default DropDownWrapper;
